import { createORPCClient, ORPCError } from '@orpc/client';
import type { ContractRouterClient } from '@orpc/contract';
import { OpenAPILink } from '@orpc/openapi-client/fetch';
import { usedVehicleContract } from 'used-vehicle-contracts';
import { resolveApiBaseUrl } from '../env-config';
import { endSession, LOGIN_PATH, REFRESH_PATH, refreshOnce } from './session-refresh';
import { authTokenStore } from './auth-token';

export { LOGIN_PATH, REFRESH_PATH };

export const isUnauthorizedError = (error: unknown): boolean => error instanceof ORPCError && error.status === 401;

const withBearer = (headers: Headers, accessToken: string): Headers => {
  const next = new Headers(headers);

  next.set('authorization', `Bearer ${accessToken}`);

  return next;
};

/**
 * Silent refresh (plan §2.5). A 401 from an *expired access token* is not a failed call: the refresh
 * token is spent once, the original request is replayed with the new bearer, and only a refresh that the
 * service itself refuses — a disabled user, a disabled membership, a bumped token version — ends the
 * session and sends the browser to `/login`.
 *
 * The renewal itself lives in `session-refresh.ts`, shared with the document upload and download paths,
 * so a burst that mixes oRPC calls with a photo upload still spends the refresh token exactly once.
 *
 * `auth.login` and `auth.refresh` are excluded: their 401 *is* the answer.
 */
const authenticatedFetch = async (
  request: Request,
  init: { redirect?: Request['redirect'] },
  _options: unknown,
  path: readonly string[],
): Promise<Response> => {
  const isAuthExchange = path[0] === 'auth' && (path[1] === 'login' || path[1] === 'refresh');

  // Buffered once, up front: a `Request` body is a stream, and replaying the call after a refresh would
  // otherwise fail on a body that the first attempt already consumed.
  const body = request.method === 'GET' || request.method === 'HEAD' ? undefined : await request.arrayBuffer();

  const send = (token: string | null): Promise<Response> =>
    fetch(request.url, {
      method: request.method,
      headers: token ? withBearer(request.headers, token) : request.headers,
      body,
      ...init,
    });

  const response = await send(authTokenStore.getAccessToken());

  if (response.status !== 401 || isAuthExchange) {
    return response;
  }

  const session = await refreshOnce();

  if (!session) {
    endSession();

    return response;
  }

  const retried = await send(session.accessToken);

  if (retried.status === 401) {
    endSession();
  }

  return retried;
};

const link = new OpenAPILink(usedVehicleContract, {
  url: resolveApiBaseUrl(),
  fetch: authenticatedFetch,
});

export const orpcClient: ContractRouterClient<typeof usedVehicleContract> = createORPCClient(link);
